(() => {
  'use strict';
  if (customElements.get('fs-discovery-dialogs')) return;
  const assetLoads = new Map();
  function loadAsset(value, kind) {
    const url = new URL(value, location.origin);
    if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) return Promise.reject(new Error('Invalid asset'));
    if (assetLoads.has(url.href)) return assetLoads.get(url.href);
    const promise = new Promise((resolve, reject) => {
      const existing = Array.from(document.querySelectorAll(kind === 'style' ? 'link[rel="stylesheet"]' : 'script[src]')).find(n => (kind === 'style' ? n.href : n.src) === url.href);
      if (existing) { resolve(); return; }
      const node = document.createElement(kind === 'style' ? 'link' : 'script');
      const timer = setTimeout(() => { node.remove(); reject(new Error('Asset timeout')); }, 15000);
      node.onload = () => { clearTimeout(timer); resolve(); };
      node.onerror = () => { clearTimeout(timer); node.remove(); reject(new Error('Asset failed')); };
      if (kind === 'style') { node.rel = 'stylesheet'; node.href = url.href; }
      else { node.src = url.href; node.async = true; }
      document.head.append(node);
    }).catch(error => { assetLoads.delete(url.href); throw error; });
    assetLoads.set(url.href, promise);
    return promise;
  }
  class DiscoveryDialogs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog?.showModal) return;
      this.ready = true;
      this.content = this.querySelector('[data-discovery-content]');
      this.status = this.querySelector('[data-discovery-status]');
      this.retry = this.querySelector('[data-discovery-retry]');
      this.closeButton = this.querySelector('[data-discovery-close]');
      this.onDocumentClick = event => {
        if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('.fsb-bundle[href], .fs-bundle-entry[href], .fsb-fit[href], .fs-fit-nav a[href]');
        if (!link) return;
        const url = new URL(link.href, location.origin);
        const current = new URL(location.href);
        const view = url.searchParams.get('view');
        if (url.origin !== location.origin || url.pathname !== this.dataset.root || !['fit', 'bundle'].includes(view) || current.searchParams.get('view') === view) return;
        const opener = link.closest('fs-popup-menu')?.querySelector('[data-fs-menu-trigger]') || link;
        if (view === 'bundle') {
          const host = document.querySelector('fs-collection-bundle-dialog');
          if (!host?.connected || typeof host.open !== 'function') return;
          event.preventDefault();
          host.open(opener);
        } else {
          if (document.querySelector('main .fs-fit-host')) return;
          event.preventDefault();
          this.open(opener);
        }
      };
      this.onClick = event => {
        if (event.target.closest('dialog') !== this.dialog) return;
        if (event.target.closest('[data-discovery-close]')) this.close();
        if (event.target.closest('[data-discovery-retry]')) this.load();
      };
      this.onCancel = event => { if (event.target === this.dialog) { event.preventDefault(); this.close(); } };
      this.onClose = event => { if (event.target === this.dialog) this.restore(); };
      this.onFrameMessage = event => {
        if (!this.dialog.open || event.origin !== 'null' || (event.ports && event.ports.length)) return;
        const frame = this.content.querySelector('iframe.fs-fit-host__frame[data-fit-isolation="opaque-origin"]');
        const data = event.data;
        if (!frame || event.source !== frame.contentWindow || !data || typeof data !== 'object' || Array.isArray(data)) return;
        const keys = Reflect.ownKeys(data);
        if (keys.length !== 1 || keys[0] !== 'type' || data.type !== 'fullstop:fit-discovery-close' || this.content.querySelector('dialog[open]')) return;
        this.close();
      };
      this.onHistory = () => { this.close(); document.querySelector('fs-collection-bundle-dialog')?.close?.(); };
      this.onKey = event => {
        if (event.key !== 'Tab' || event.target.closest('dialog') !== this.dialog) return;
        const items = Array.from(this.dialog.querySelectorAll('button:not([disabled]), a[href], iframe, [tabindex="0"]')).filter(n => !n.closest('[hidden]') && n.getClientRects().length && n.closest('dialog') === this.dialog);
        const first = items[0] || this.closeButton, last = items[items.length - 1] || first;
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      };
      document.addEventListener('click', this.onDocumentClick);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKey);
      window.addEventListener('popstate', this.onHistory);
      window.addEventListener('message', this.onFrameMessage);
    }
    disconnectedCallback() {
      if (!this.ready) return;
      this.controller?.abort(); this.close(); this.restore();
      document.removeEventListener('click', this.onDocumentClick);
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('keydown', this.onKey);
      window.removeEventListener('popstate', this.onHistory);
      window.removeEventListener('message', this.onFrameMessage);
      this.ready = false;
    }
    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener;
      this.saved = { x: scrollX, y: scrollY, overflow: document.documentElement.style.overflow, behavior: document.documentElement.style.scrollBehavior, body: ['overflow', 'position', 'top', 'left', 'width'].map(key => [key, document.body.style[key]]) };
      document.documentElement.style.overflow = 'hidden';
      Object.assign(document.body.style, { overflow: 'hidden', position: 'fixed', top: -this.saved.y + 'px', left: -this.saved.x + 'px', width: '100%' });
      try { this.dialog.showModal(); } catch (_) { this.restore(); return; }
      this.closeButton.focus({ preventScroll: true });
      this.load();
    }
    close() {
      const product = this.content.querySelector('fs-size-product');
      if (product?.cartPending) return;
      product?.close?.();
      if (this.dialog.open) this.dialog.close();
      this.restore();
    }
    restore() {
      if (!this.saved) return;
      const saved = this.saved; this.saved = null;
      saved.body.forEach(([key, value]) => { document.body.style[key] = value; });
      document.documentElement.style.overflow = saved.overflow;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
      document.documentElement.style.scrollBehavior = saved.behavior;
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }
    async load() {
      if (this.loaded || this.loading) return;
      this.loading = true; this.retry.hidden = true; this.status.hidden = false;
      this.status.textContent = this.dataset.loading;
      this.content.setAttribute('aria-busy', 'true');
      const controller = new AbortController(); this.controller = controller;
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const url = new URL(this.dataset.sectionUrl, location.origin);
        if (url.origin !== location.origin || url.searchParams.get('section_id') !== 'fs-find-fit') throw new Error('Invalid section');
        const [response] = await Promise.all([fetch(url.href, { credentials: 'same-origin', signal: controller.signal }), loadAsset(this.dataset.fitScript, 'script'), loadAsset(this.dataset.productScript, 'script'), loadAsset(this.dataset.fitStyle, 'style'), loadAsset(this.dataset.productStyle, 'style')]);
        if (!response.ok) throw new Error('Section unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const hosts = parsed.querySelectorAll('.fs-fit-host[data-fs-size-shop]');
        if (hosts.length !== 1 || !hosts[0].querySelector('iframe[sandbox="allow-scripts"]')) throw new Error('Missing size finder');
        hosts[0].querySelectorAll('script:not([type="application/json"])').forEach(node => node.remove());
        if (!this.isConnected || controller.signal.aborted) return;
        this.content.replaceChildren(document.importNode(hosts[0], true));
        this.loaded = true; this.status.hidden = true;
      } catch (_) {
        if (this.isConnected) { this.status.textContent = this.dataset.error; this.retry.hidden = false; }
      } finally { clearTimeout(timer); this.loading = false; this.content.setAttribute('aria-busy', 'false'); }
    }
  }
  customElements.define('fs-discovery-dialogs', DiscoveryDialogs);
})();
